import { useEffect, useState } from "react";
import {
  fetchPhotos,
  createPhoto,
  updatePhoto,
  deletePhoto,
  resolveImage,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, Loader2, Trash2, Save } from "lucide-react";

const PhotoRow = ({ photo, onChange, onDelete }) => {
  const [title, setTitle] = useState(photo.title || "");
  const [caption, setCaption] = useState(photo.caption || "");
  const [category, setCategory] = useState(photo.category || "General");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const updated = await updatePhoto(photo.id, { title, caption, category });
      onChange(updated);
      toast.success("Frame updated.");
    } catch {
      toast.error("Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirm("Delete this photograph? This cannot be undone.")) return;
    setBusy(true);
    try {
      await deletePhoto(photo.id);
      onDelete(photo.id);
      toast.success("Frame removed.");
    } catch {
      toast.error("Could not delete.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`photo-row-${photo.id}`}
      className="grid grid-cols-12 gap-6 py-6 border-b border-[#e8aa42]/10"
    >
      <div className="col-span-4 md:col-span-3">
        <div className="aspect-square overflow-hidden border border-[#e8aa42]/20 bg-[#1a1614] relative">
          <img
            src={resolveImage(photo.image_url)}
            alt={title || "photo"}
            className="w-full h-full object-cover"
          />
          {photo.is_example && (
            <span
              data-testid={`curator-example-badge-${photo.id}`}
              className="absolute top-2 left-2 font-ui text-[9px] tracking-eerie uppercase bg-[#0f0c0b]/85 text-[#e8aa42] px-2 py-1 border border-[#e8aa42]/40"
            >
              Example
            </span>
          )}
        </div>
      </div>
      <div className="col-span-8 md:col-span-9 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="font-ui text-[10px] tracking-eerie uppercase text-[#a89f91]">
              Title
            </Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid={`photo-title-${photo.id}`}
              className="mt-1 bg-[#1a1614] border-[#e8aa42]/20 text-[#e2d6c8] focus-visible:ring-[#e8aa42]/40 focus-visible:border-[#e8aa42]/60"
              placeholder="Optional"
            />
          </div>
          <div>
            <Label className="font-ui text-[10px] tracking-eerie uppercase text-[#a89f91]">
              Category
            </Label>
            <Input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid={`photo-category-${photo.id}`}
              className="mt-1 bg-[#1a1614] border-[#e8aa42]/20 text-[#e2d6c8] focus-visible:ring-[#e8aa42]/40 focus-visible:border-[#e8aa42]/60"
              placeholder="e.g. Landscape"
            />
          </div>
        </div>
        <div>
          <Label className="font-ui text-[10px] tracking-eerie uppercase text-[#a89f91]">
            Caption
          </Label>
          <Textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={2}
            data-testid={`photo-caption-${photo.id}`}
            className="mt-1 bg-[#1a1614] border-[#e8aa42]/20 text-[#e2d6c8] focus-visible:ring-[#e8aa42]/40 focus-visible:border-[#e8aa42]/60"
            placeholder="Optional"
          />
        </div>
        <div className="flex gap-3">
          <Button
            onClick={save}
            disabled={busy}
            data-testid={`photo-save-${photo.id}`}
            className="bg-[#8b0000] hover:bg-[#5c0000] text-[#e2d6c8] font-ui text-[11px] tracking-eerie uppercase rounded-none h-10 px-6 border border-[#8b0000] hover:border-[#e8aa42]/60"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />}
            Save
          </Button>
          <Button
            onClick={remove}
            disabled={busy}
            data-testid={`photo-delete-${photo.id}`}
            variant="ghost"
            className="text-[#a89f91] hover:text-[#e2d6c8] hover:bg-[#8b0000]/10 font-ui text-[11px] tracking-eerie uppercase rounded-none h-10 px-4"
          >
            <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function CuratorPhotos() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState("General");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchPhotos()
      .then(setPhotos)
      .catch(() => toast.error("Could not fetch photographs."))
      .finally(() => setLoading(false));
  }, []);

  const upload = async () => {
    if (!file) return toast.error("Choose an image first.");
    setUploading(true);
    try {
      const created = await createPhoto({ file, title, caption, category });
      setPhotos((prev) => [created, ...prev]);
      setFile(null);
      setTitle("");
      setCaption("");
      setCategory("General");
      const inp = document.getElementById("photo-file-input");
      if (inp) inp.value = "";
      toast.success("Photograph hung in the gallery.");
    } catch {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-10">
      {/* Uploader */}
      <div
        data-testid="photo-uploader"
        className="border border-[#e8aa42]/20 bg-[#1a1614]/50 p-6 md:p-8"
      >
        <p className="font-ui text-xs tracking-eerie uppercase text-[#e8aa42]/80 mb-6">
          Hang a New Frame
        </p>
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-6">
            <Label className="font-ui text-[10px] tracking-eerie uppercase text-[#a89f91]">
              Image
            </Label>
            <label
              htmlFor="photo-file-input"
              className="mt-2 flex items-center justify-center gap-2 cursor-pointer border border-dashed border-[#e8aa42]/40 hover:border-[#e8aa42] text-[#e8aa42] font-ui text-xs tracking-eerie uppercase py-4 transition-colors"
            >
              <Upload className="w-4 h-4" strokeWidth={1.5} />
              {file ? file.name : "Choose an image"}
              <input
                id="photo-file-input"
                data-testid="photo-file-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
          </div>
          <div className="col-span-6 md:col-span-3">
            <Label className="font-ui text-[10px] tracking-eerie uppercase text-[#a89f91]">
              Title (optional)
            </Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="photo-new-title"
              className="mt-2 bg-[#1a1614] border-[#e8aa42]/20 text-[#e2d6c8] focus-visible:ring-[#e8aa42]/40"
            />
          </div>
          <div className="col-span-6 md:col-span-3">
            <Label className="font-ui text-[10px] tracking-eerie uppercase text-[#a89f91]">
              Category
            </Label>
            <Input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="photo-new-category"
              placeholder="General"
              className="mt-2 bg-[#1a1614] border-[#e8aa42]/20 text-[#e2d6c8] focus-visible:ring-[#e8aa42]/40"
            />
          </div>
          <div className="col-span-12">
            <Label className="font-ui text-[10px] tracking-eerie uppercase text-[#a89f91]">
              Caption (optional)
            </Label>
            <Textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={2}
              data-testid="photo-new-caption"
              className="mt-2 bg-[#1a1614] border-[#e8aa42]/20 text-[#e2d6c8] focus-visible:ring-[#e8aa42]/40"
            />
          </div>
          <div className="col-span-12">
            <Button
              onClick={upload}
              disabled={uploading || !file}
              data-testid="photo-upload-btn"
              className="bg-[#8b0000] hover:bg-[#5c0000] text-[#e2d6c8] font-ui text-xs tracking-eerie uppercase rounded-none h-11 px-8 border border-[#8b0000] hover:border-[#e8aa42]/60"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Hanging…
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} /> Hang the Frame
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* List */}
      <div>
        <p className="font-ui text-xs tracking-eerie uppercase text-[#e8aa42]/80 mb-4">
          The Gallery · {photos.length} {photos.length === 1 ? "Frame" : "Frames"}
        </p>
        {loading ? (
          <div className="font-ui text-xs tracking-eerie uppercase text-[#a89f91] py-8">
            Loading photographs…
          </div>
        ) : photos.length === 0 ? (
          <div className="font-body text-[#a89f91] py-6 border-t border-[#e8aa42]/10">
            No photographs yet. Add your first frame above.
          </div>
        ) : (
          photos.map((p) => (
            <PhotoRow
              key={p.id}
              photo={p}
              onChange={(u) =>
                setPhotos((prev) => prev.map((x) => (x.id === u.id ? u : x)))
              }
              onDelete={(id) =>
                setPhotos((prev) => prev.filter((x) => x.id !== id))
              }
            />
          ))
        )}
      </div>
    </div>
  );
}
